export const SERVICE_TYPES = ['web', 'static', 'private', 'worker', 'cron', 'redis'];
const INTENT_KEY = 'replov:deployment-intent';

const BILLING_TYPES = ['hourly', 'monthly'];
const safePlan = value => typeof value === 'string' && /^[a-z0-9-]{1,40}$/.test(value) ? value : null;

export function deploymentPath(type = 'web', options = {}) {
  const params = new URLSearchParams({ create: SERVICE_TYPES.includes(type) ? type : 'web' });
  const plan = safePlan(options.plan);
  if (plan) params.set('plan', plan);
  if (BILLING_TYPES.includes(options.billing)) params.set('billing', options.billing);
  return `/dashboard?${params.toString()}`;
}

// Only supported creation intents are persisted; arbitrary redirect URLs are never accepted.
export function normalizeDeploymentPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/dashboard?')) return null;
  const params = new URLSearchParams(value.slice(value.indexOf('?')));
  const type = params.get('create');
  if (!SERVICE_TYPES.includes(type)) return null;
  return deploymentPath(type, { plan: params.get('plan'), billing: params.get('billing') });
}

// The challenge registration route also needs a safe post-login return path.
// Keep the allow-list narrow and preserve only campaign attribution keys.
export function normalizeChallengePath(value) {
  if (typeof value !== 'string' || !value.startsWith('/hackathon/register')) return null;
  const [pathname, rawQuery = ''] = value.split('?', 2);
  if (pathname !== '/hackathon/register') return null;
  const incoming = new URLSearchParams(rawQuery);
  const safe = new URLSearchParams();
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
    const entry = incoming.get(key);
    if (entry && entry.length <= 200) safe.set(key, entry);
  });
  const query = safe.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function rememberDeployment(path) {
  const target = normalizeDeploymentPath(path);
  if (!target) return;
  try { sessionStorage.setItem(INTENT_KEY, JSON.stringify({ target, savedAt: Date.now() })); } catch { /* Storage may be disabled. */ }
}

// The MCP connector's consent screen bounces through login when there is no
// session, and must come back to the *same* authorization request — dropping
// the query string would lose the client_id and PKCE challenge. Only this one
// pathname is allowed, and only as a same-origin path, so this stays as narrow
// as normalizeDeploymentPath and is not an open redirect.
export function normalizeOAuthConsentPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/oauth/authorize?')) return null;
  const params = new URLSearchParams(value.slice(value.indexOf('?')));
  // Must look like a real authorization request, not just the bare path.
  if (!params.get('client_id') || !params.get('code_challenge')) return null;
  return value;
}

export function authDestination(search = '') {
  const next = new URLSearchParams(search).get('next');
  const consent = normalizeOAuthConsentPath(next);
  if (consent) return consent;
  const challenge = normalizeChallengePath(next);
  if (challenge) return challenge;
  const explicit = normalizeDeploymentPath(next);
  if (explicit) return explicit;
  try {
    const saved = JSON.parse(sessionStorage.getItem(INTENT_KEY));
    if (saved && Date.now() - saved.savedAt < 60 * 60 * 1000) {
      return normalizeDeploymentPath(saved.target) || '/dashboard';
    }
  } catch { /* Missing or unavailable storage should not block login. */ }
  return '/dashboard';
}

export function clearDeploymentIntent() {
  try { sessionStorage.removeItem(INTENT_KEY); } catch { /* Optional storage. */ }
}

export function trackDecision(action, context = {}) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', `website_${action}`, context);
    }
  } catch { /* Analytics must never block navigation. */ }
}
